import { useEffect } from 'react';
import { getBrandFontsKey, loadBrandFonts } from '../utils/brandFonts';

/**
 * Loads the event's brand fonts (K22) and sets their CSS variables on `<html>` (PB-10474, LG-01). The variables go
 * on `<html>`, not on the gallery layout, because Reactstrap modals (the capture overlay, the ZIP dialog) render into
 * `<body>` outside it. With no `brandFonts` nothing is loaded or set, so the gallery renders as today. Unmount, or new
 * font files, removes the variables and the faces this hook added.
 * @param {{ heading: object|null, body: object|null }|null|undefined} brandFonts - The event's `brandFonts`.
 */
const useBrandFonts = (brandFonts) => {
  // Only a change of font files reloads. A gallery refresh returns a new object with the same files.
  const brandFontsKey = getBrandFontsKey(brandFonts);

  useEffect(() => {
    if (!brandFontsKey) {
      return undefined;
    }

    let isCancelled = false;
    let appliedVariables = [];
    let addedFontFaces = [];
    const { style } = document.documentElement;
    // Every role reuses one alias, so a face from the previous fonts must go, or the next family would mix with it.
    const removeFontFaces = (fontFaces) => fontFaces.forEach((fontFace) => document.fonts?.delete(fontFace));

    loadBrandFonts(brandFonts).then(({ variables, fontFaces }) => {
      if (isCancelled) {
        removeFontFaces(fontFaces);
        return;
      }

      addedFontFaces = fontFaces;
      appliedVariables = Object.keys(variables);
      appliedVariables.forEach((name) => style.setProperty(name, variables[name]));
    });

    return () => {
      isCancelled = true;
      appliedVariables.forEach((name) => style.removeProperty(name));
      removeFontFaces(addedFontFaces);
    };
  }, [brandFontsKey]);
};

export default useBrandFonts;
